import { createSyncStore, type Adapter } from '@frontend/shell-utils';
import { createShallowStore } from '@frontend/store';
import { predefinedThemes, defaultNonShellTheme } from './constant';
import type { ShellColorTheme } from './types';
import { getRandomColorTheme } from './utils';

export type ShellColor = keyof typeof predefinedThemes;
type ShellThemes = Record<ShellColor, ShellColorTheme>;

declare global {
  interface Window {
    shell?: Adapter;
  }
}

interface ShellColorStore {
  colorObject: ShellColorTheme;
  allColors: ShellThemes;
  setShellColor: (color: ShellColor | ShellColorTheme) => void;
  randomizeTheme: (baseColor?: string, maxNumOfColors?: number) => void;
}

const hasShellThemeEnabled = window?.shell?.isShell && window?.shell?.featureAvailability?.has('shell-theme');

export const useShellThemeStore = createSyncStore<
  Pick<ShellColorStore, 'colorObject' | 'allColors'>,
  Pick<ShellColorStore, 'setShellColor' | 'randomizeTheme'>
>({
  channel: 'shell-theme',
  syncKeys: ['colorObject'],
  throttleMs: 50,
  syncOptions: { broadcastInitialState: false },
  state: {
    colorObject: hasShellThemeEnabled ? predefinedThemes.seaweedColor : defaultNonShellTheme,
    allColors: predefinedThemes,
  },
  actions: (set) => ({
    setShellColor: (color: ShellColor | ShellColorTheme) => {
      if (typeof color === 'string') {
        set({ colorObject: predefinedThemes[color] });
      } else {
        set({ colorObject: color });
      }
    },
    randomizeTheme: (baseColor?: string, maxNumOfColors?: number) => {
      const randomTheme = getRandomColorTheme(baseColor, maxNumOfColors);
      set({ colorObject: randomTheme });
    },
  }),
});

export const useShellTheme = createShallowStore(useShellThemeStore);
